/**
 * STAND-IN for a link form of Maritime `Button` — 0.2.0's Button renders only a <button>
 * (no `href` / `asChild`). Delete this file once Maritime has one.
 *
 * A navigation that looks like a Button. `to` is an in-app route (router Link); `href` is
 * anything else (plain <a>). Navigation stays a link, so middle-click and "open in new tab"
 * work. Styled from the same theme variables as Maritime's Button (shell.css → Stand-ins).
 */

import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface LinkButtonBaseProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md';
  startIcon?: ReactNode;
  children: ReactNode;
}

export type LinkButtonProps = LinkButtonBaseProps & ({ to: string; href?: never } | { href: string; to?: never });

export function LinkButton({
  to,
  href,
  variant = 'primary',
  size = 'md',
  startIcon,
  className,
  children,
  ...rest
}: LinkButtonProps) {
  const classes = ['shell-link-button', `shell-link-button--${variant}`, `shell-link-button--${size}`, className]
    .filter(Boolean)
    .join(' ');
  const content = (
    <>
      {startIcon && (
        <span className="shell-link-button__icon" aria-hidden>
          {startIcon}
        </span>
      )}
      {children}
    </>
  );

  return to !== undefined ? (
    <Link to={to} className={classes} {...rest}>
      {content}
    </Link>
  ) : (
    <a href={href} className={classes} {...rest}>
      {content}
    </a>
  );
}
